import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import CommonSelectLite from "components/Common/CommonSelectLite";

import AddEditTeacherJournalModal from "../modals/AddEditTeacherJournalModal";
import TeacherJournalFlipBook from "../components/TeacherJournalFlipBook";

import useTeacherJournal from "../hooks/useTeacherJournal";
import useClassTeacher from "feature-module/academic/classes/hooks/useClassTeacher";

import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";
import { ClassData } from "types/classTypes";
import { TeacherJournal } from "types/teacherJournalTypes";
import CustomAlert from "components/Common/ui/CustomAlert";
import { getSubjectLabelFromCode } from "utils/schoolUtils";

const TeacherJournalFlipBookPage = () => {
    const { t } = useTranslation();
    const ability = useAbility();

    const teacherId = useSelector(
        (state: any) => state.authSlice.user?.roleId
    );

    const {
        journals,
        loading,
        selectedJournal,
        setSelectedJournal,
        fetchTeacherJournals,
        handleCreate,
        handleUpdate,
        handleDelete,
    } = useTeacherJournal();

    const { classes, fetchTeacherClasses } = useClassTeacher();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
    const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(
        null
    );

    const baseParams = {
        page: 1,
        limit: 100,
        type: "book",
    };

    useEffect(() => {
        if (!teacherId) return;
        fetchTeacherClasses({ page: 1, limit: 1000 });
    }, [teacherId]);

    useEffect(() => {
        if (!teacherId || !selectedClassId || !selectedSubjectId) return;
        fetchTeacherJournals({
            ...baseParams,
            teacherId,
            classId: selectedClassId,
            subjectId: selectedSubjectId,
        });
    }, [
        teacherId,
        selectedClassId,
        selectedSubjectId,
        fetchTeacherJournals,
    ]);

    const breadcrumbItems = [
        { label: "dashboard", path: all_routes.dashboard },
        { label: t("teacherJournal.my_teacher_journal"), path: all_routes.myTeacherJournal },
    ];

    const classOptions = useMemo(
        () =>
            (classes || []).map((c: ClassData) => ({
                label: c.name,
                value: c.id,
            })),
        [classes]
    );

    const subjectOptions = useMemo(() => {
        if (!selectedClassId || !classes || classes.length === 0) return [];

        const cls = classes.find((c: any) => c.id === selectedClassId);
        if (!cls || !cls.classSubject) return [];

        return cls.classSubject
            .filter(
                (cs: any) =>
                    cs.subject && (cs.teacher.id === teacherId)
            )
            .map((cs: any) => ({
                label: getSubjectLabelFromCode(cs.subject.code),
                value: cs.subject.id,
            }));
    }, [classes, selectedClassId, teacherId]);

    const handleClassChange = (classId: string) => {
        setSelectedClassId(classId);
        setSelectedSubjectId(null);
        setSelectedJournal(null);
    };

    const handleSubjectChange = (subjectId: string) => {
        setSelectedSubjectId(subjectId);
        setSelectedJournal(null);
    };

    const handleSaveJournal = async (journalData: any) => {
        let res = false;

        if (selectedJournal) {
            res = await handleUpdate(selectedJournal.id, journalData);
        } else {
            const payload = { ...journalData, teacherId, subjectId: selectedSubjectId, classId: selectedClassId }

            res = await handleCreate(payload);
        }

        if (res) {
            setIsModalOpen(false);
            setSelectedJournal(null);

            if (teacherId && selectedClassId && selectedSubjectId) {
                fetchTeacherJournals({
                    ...baseParams,
                    teacherId,
                    classId: selectedClassId,
                    subjectId: selectedSubjectId,
                });
            }
        }
    };

    const handleEdit = (record: TeacherJournal) => {
        setSelectedJournal(record);
        setIsModalOpen(true);
    };

    const handleDeleteWrapper = async (id: string) => {
        const res = await handleDelete(id);
        if (res && teacherId && selectedClassId && selectedSubjectId) {
            if (selectedJournal?.id === id) {
                setSelectedJournal(null);
            }
            fetchTeacherJournals({
                ...baseParams,
                teacherId,
                classId: selectedClassId,
                subjectId: selectedSubjectId,
            });
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <PageHeader
                    title={t("teacherJournal.my_teacher_journal")}
                    breadcrumbItems={breadcrumbItems}
                />
                {ability.can("create", "TeacherJournal") && (
                    <div className="d-flex justify-content-end mb-3">
                        <CustomButton
                            onClick={() => {
                                setSelectedJournal(null);
                                setIsModalOpen(true);
                            }}
                            disabled={!selectedClassId || !selectedSubjectId}
                        >
                            <i className="ti ti-plus me-2" />
                            {t("teacherJournal.add_title")}
                        </CustomButton>
                    </div>
                )}

                <div className="mb-4">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row align-items-end">

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">
                                        {t("teacherJournal.form.class")}
                                    </label>
                                    <CommonSelectLite
                                        options={classOptions}
                                        optionLabelKey="label"
                                        optionValueKey="value"
                                        value={selectedClassId}
                                        onChange={handleClassChange}
                                        placeholder={t("teacherJournal.form.select_class")}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">
                                        {t("teacherJournal.form.subject")}
                                    </label>
                                    <CommonSelectLite
                                        options={subjectOptions}
                                        optionLabelKey="label"
                                        optionValueKey="value"
                                        value={selectedSubjectId}
                                        onChange={handleSubjectChange}
                                        disabled={!selectedClassId}
                                        placeholder={
                                            selectedClassId
                                                ? t("teacherJournal.form.select_subject")
                                                : t("teacherJournal.form.select_class_first")
                                        }
                                    />
                                </div>

                            </div>
                        </div>
                    </div>
                </div>




                {!selectedClassId || !selectedSubjectId ? (
                    <CustomAlert
                        message={t("alert.teacherJournal_no_selection.message")}
                        description={t("alert.teacherJournal_no_selection.description")}
                        type="warning"
                        showIcon
                    />

                ) : (
                    <TeacherJournalFlipBook
                        dataSource={journals ?? []}
                        loading={loading}
                        onEdit={handleEdit}
                        onDelete={handleDeleteWrapper}
                    />
                )}

                <AddEditTeacherJournalModal
                    forTeacher={true}
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    journalData={selectedJournal}
                    onSave={handleSaveJournal}
                    classes={classes}
                />
            </div>
        </div>
    );
};

export default TeacherJournalFlipBookPage;
